// The MCP App shell mounts the player with ?pix_scope=<idaccount,...> when the
// recipe came from a custom MCP. Both recommendation surfaces (chain, Pixie)
// send it back explicitly as publication_scope so nothing outside the MCP's
// publishers is ever suggested.
function params() {
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams("");
  }
}

// Both consumers of the scope reject a longer list: the /chain request body and
// the Pixie chat query param each declare max_length=200.
const MAX_SCOPE_IDS = 200;

export function readMcpScope(): number[] {
  const raw = params().get("pix_scope");
  if (!raw) {
    return [];
  }
  const out: number[] = [];
  raw.split(",").forEach(function (part) {
    const n = parseInt(part, 10);
    if (n > 0 && out.indexOf(n) === -1) {
      out.push(n);
    }
  });
  return out.slice(0, MAX_SCOPE_IDS);
}

// Mirrors the slug grammar the MCP router and the backend validator accept
// (pix-arcs-api app/mcp_servers/slugs.py), so a hand-edited or malformed
// pix_mcp is dropped rather than carried into analytics.
export function readMcpSlug(): string | null {
  const slug = params().get("pix_mcp");
  return slug && /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/.test(slug)
    ? slug
    : null;
}

export interface McpAppProps {
  pix_surface?: string;
  pix_host?: string;
  pix_mcp?: string;
}

let mcpAppProps: Readonly<McpAppProps> | null = null;

/**
 * MCP-App embedding context from the player's own URL: pix_surface, pix_host
 * and the validated pix_mcp slug, each included only when present. Read by both
 * analytics sinks — Mixpanel registers them as super-properties, Umami gets
 * them merged into each event's payload.
 *
 * Memoised: the player never rewrites its own query string, so the answer is
 * fixed for the pageload and Umami's per-event read costs nothing after the
 * first. Every caller shares the one object, so it is frozen — merge into a
 * copy, never into the returned value.
 */
export function readMcpAppProps(): Readonly<McpAppProps> {
  if (mcpAppProps) {
    return mcpAppProps;
  }
  const out: McpAppProps = {};
  const search = params();
  const surface = search.get("pix_surface");
  const host = search.get("pix_host");
  const slug = readMcpSlug();
  if (surface) {
    out.pix_surface = surface;
  }
  if (host) {
    out.pix_host = host;
  }
  // Fail closed the way the shell does (app/mcp-app/shell.ts readCurrentMcp):
  // a slug without a usable scope would tag every event as belonging to the
  // custom MCP while the chain and Pixie ran unscoped.
  if (slug && readMcpScope().length) {
    out.pix_mcp = slug;
  }
  mcpAppProps = Object.freeze(out);
  return mcpAppProps;
}
